import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { useTheme, useMediaQuery } from '@material-ui/core';
import { AuthContext } from '@ryanar/react-auth-provider';
import sessionStore from 'store/sessionStore';
import TimerCountDown from 'utils/timer';
import { PROFILE_INACTIVE_MSG, SESSION_TIMEOUT_MSG } from 'utils/constants';
import Button from 'ui/Button/Button';
import ModalWindow from '../ModalWindow/ModalWindow';

import styles from './ForcibleLogoutModal.module.css';

import warningIcon from 'assets/icons/warning.png';

const ForcibleLogoutModal = () => {
  const timer = useMemo(() => new TimerCountDown(), []);
  const [timeLeft, setTimeLeft] = useState<number | undefined>();

  const timeIsOver = useMemo(() => timeLeft != null && timeLeft <= 0, [timeLeft]);

  const [open, setOpen] = useState(false);
  const smallScreen = useMediaQuery(useTheme().breakpoints.down('sm'));

  const { setAuthenticated } = useContext(AuthContext);
  const { isSessionTimeout, isProfileActive } = sessionStore;

  const requestRef = useRef<number | undefined>();

  const animate: FrameRequestCallback = () => {
    setTimeLeft(timer.time);
    requestRef.current = requestAnimationFrame(animate);
  };

  const stopTimer = useCallback(() => {
    timer.stop();
    if (requestRef.current != null) {
      cancelAnimationFrame(requestRef.current);
    }
  }, [timer]);

  const handleOnClose = useCallback(() => {
    stopTimer();
    setOpen(false);
    setAuthenticated(false);
  }, [setAuthenticated, stopTimer]);

  useEffect(() => {
    if (isSessionTimeout || !isProfileActive) {
      setOpen(true);
      timer.start(3);
      requestRef.current = requestAnimationFrame(animate);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSessionTimeout, isProfileActive]);

  useEffect(() => {
    if (timeIsOver) {
      handleOnClose();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeIsOver]);

  const getMessageText = () => {
    if (isSessionTimeout) {
      return SESSION_TIMEOUT_MSG;
    }
    if (!isProfileActive) {
      return PROFILE_INACTIVE_MSG;
    }
    return '';
  };

  const content = (
    <section className={styles.content}>
      <div className={styles.container}>
        <img src={warningIcon} alt="warning-icon" />
        <span className={styles.message}>
          {getMessageText()}
        </span>
      </div>
      <div className={styles.buttonContainer}>
        {smallScreen && (
          <Button className={styles.button} onClick={handleOnClose} fullWidth>
            Continue
          </Button>
        )}
      </div>
    </section>
  );

  if (!open) {
    return null;
  }

  return (
    <ModalWindow open={open} onClose={handleOnClose} className={styles.modalContainer} fullScreen>
      {content}
    </ModalWindow>
  );
};

export default observer(ForcibleLogoutModal);
